// Schedule-conflict detection for broiler batches vs both other broiler
// batches (hard conflict) and active layer batches in the same brooder/
// schooner slots (soft conflict). Lifted verbatim from main.jsx — the
// \u2013 escape literals are deliberate (§10 don't-touch list).

import {addDays, toISO, fmtS} from './dateUtils.js';
import {calcTimeline, resolveBrooderEntryDate, overlaps, BROODER_CLEANOUT, SCHOONER_CLEANOUT} from './broiler.js';

// The BatchForm fields this detector reads. Dates are ISO 'YYYY-MM-DD' strings
// (or empty while the form is incomplete), and brooder/schooner are the slot ids
// from RESOURCES.
export type ConflictForm = {
  hatchDate?: string | null;
  brooderIn?: string | null;
  breed?: string | null;
  processingDate?: string | null;
  growoutWeeks?: number | string | null;
  brooderDays?: number | string | null;
  brooder?: string | null;
  schooner?: string | null;
};

// An existing broiler batch, reduced to the slot occupancy this compares.
export type BroilerBatchRow = {
  id?: unknown;
  name?: string | null;
  brooder?: string | null;
  brooderIn?: string | null;
  brooderOut?: string | null;
  schooner?: string | null;
  schoonerIn?: string | null;
  schoonerOut?: string | null;
};

// A layer batch occupying the same physical brooders/schooners. Its slot names
// carry a 'Brooder '/'Schooner ' prefix that is stripped before comparison.
export type LayerBatchRow = {
  name?: string | null;
  status?: string | null;
  brooder_name?: string | null;
  schooner_name?: string | null;
  brooder_entry_date?: string | null;
  brooder_exit_date?: string | null;
  schooner_entry_date?: string | null;
  schooner_exit_date?: string | null;
};

// `soft` false is a hard broiler-vs-broiler collision (blocks save without
// force); true is a broiler-vs-layer overlap (a warning).
export type ScheduleConflict = {soft: boolean; message: string};

export function detectConflicts(
  form: ConflictForm,
  batches: BroilerBatchRow[],
  layerBatches: LayerBatchRow[] | null | undefined,
  editId?: unknown,
): ScheduleConflict[] {
  const tl = calcTimeline(form.hatchDate, form.breed, form.processingDate, form.growoutWeeks, form.brooderDays);
  if (!tl) return [];
  const brooderIn = resolveBrooderEntryDate(form.hatchDate, form.brooderIn);
  const safeAddDays = (dateStr: string | null | undefined, n: number): string | null => {
    if (!dateStr) return null;
    try {
      return toISO(addDays(dateStr, n));
    } catch (_e) {
      return null;
    }
  };
  const bEnd = safeAddDays(tl.brooderOut, BROODER_CLEANOUT);
  const sEnd = safeAddDays(tl.schoonerOut, SCHOONER_CLEANOUT);
  if (!bEnd && !sEnd) return [];
  const out: ScheduleConflict[] = [];
  // Hard conflicts: broiler vs broiler
  for (const b of batches) {
    if (b.id === editId) continue;
    if (b.brooder === form.brooder && b.brooderIn && b.brooderOut && bEnd) {
      const exEnd = safeAddDays(b.brooderOut, BROODER_CLEANOUT);
      if (exEnd && overlaps(brooderIn, bEnd, b.brooderIn, exEnd))
        out.push({
          soft: false,
          message:
            'Brooder ' +
            form.brooder +
            ' conflict with "' +
            b.name +
            '" (brooder ' +
            fmtS(b.brooderIn) +
            '\u2013' +
            fmtS(b.brooderOut) +
            ' + ' +
            BROODER_CLEANOUT +
            'd cleanout)',
        });
    }
    if (b.schooner === form.schooner && b.schoonerIn && b.schoonerOut && sEnd) {
      const exEnd = safeAddDays(b.schoonerOut, SCHOONER_CLEANOUT);
      if (exEnd && overlaps(tl.schoonerIn, sEnd, b.schoonerIn, exEnd))
        out.push({
          soft: false,
          message:
            'Schooner ' +
            form.schooner +
            ' conflict with "' +
            b.name +
            '" (schooner ' +
            fmtS(b.schoonerIn) +
            '\u2013' +
            fmtS(b.schoonerOut) +
            ' + ' +
            SCHOONER_CLEANOUT +
            'd cleanout)',
        });
    }
  }
  // Soft conflicts: broiler vs layer (layer brooder/schooner names have prefix to strip)
  if (layerBatches && layerBatches.length) {
    for (const lb of layerBatches) {
      if (lb.status === 'retired') continue;
      if (lb.name === 'Retirement Home') continue;
      // Strip "Brooder " / "Schooner " prefix to compare with broiler form values
      const lbBrooderId = (lb.brooder_name || '').replace(/^Brooder\s*/i, '').trim();
      const lbSchoonerId = (lb.schooner_name || '').replace(/^Schooner\s*/i, '').trim();
      // Brooder check
      if (lbBrooderId && lbBrooderId === form.brooder && lb.brooder_entry_date && bEnd) {
        const lbBOut = lb.brooder_exit_date || safeAddDays(lb.brooder_entry_date, 21);
        if (lbBOut) {
          const lbBExEnd = safeAddDays(lbBOut, BROODER_CLEANOUT);
          if (lbBExEnd && overlaps(brooderIn, bEnd, lb.brooder_entry_date, lbBExEnd))
            out.push({
              soft: true,
              message:
                'Brooder ' +
                form.brooder +
                ' overlaps layer batch "' +
                lb.name +
                '" (brooder ' +
                fmtS(lb.brooder_entry_date) +
                '\u2013' +
                fmtS(lbBOut) +
                ')',
            });
        }
      }
      // Schooner check
      if (lbSchoonerId && lbSchoonerId === form.schooner && lb.schooner_entry_date && sEnd) {
        const lbSOut = lb.schooner_exit_date || safeAddDays(lb.schooner_entry_date, 119);
        if (lbSOut) {
          const lbSExEnd = safeAddDays(lbSOut, SCHOONER_CLEANOUT);
          if (lbSExEnd && overlaps(tl.schoonerIn, sEnd, lb.schooner_entry_date, lbSExEnd))
            out.push({
              soft: true,
              message:
                'Schooner ' +
                form.schooner +
                ' overlaps layer batch "' +
                lb.name +
                '" (schooner ' +
                fmtS(lb.schooner_entry_date) +
                '\u2013' +
                fmtS(lbSOut) +
                ')',
            });
        }
      }
    }
  }
  return out;
}
